
import { defineComponent, PropType } from 'vue'
import { Dataset } from '~/types/reportTypes'
import { getColorShades } from '~/utils/ui'

const BASE_COLOR = '#1035ad'

/**
 * Component to show empty chart
 */
export default defineComponent({
  name: 'EmptyChart',

  props: {
    subtitle: {
      type: String,
      default: 'Not enough data to show a trend. Please come back later.'
    },
    count: {
      type: Number,
      default: 1
    },
    baseShade: {
      type: String,
      default: BASE_COLOR
    },
    length: {
      type: Number,
      default: 15
    },
    stacked: {
      type: Boolean,
      default: false
    },
    chartType: {
      type: String,
      default: 'line'
    },
    chartDataset: {
      type: Array as PropType<Array<Dataset>>,
      default: () => []
    },
    chartColors: {
      type: Array as PropType<Array<string>>,
      default: () => []
    },
    height: {
      type: Number,
      default: 300
    }
  },

  computed: {
    mockLabels(): Array<string> {
      return Array.from(Array(this.length).keys()).map((idx) => `Label ${idx}`)
    },

    colors(): string[] {
      if (this.chartColors.length) return this.chartColors

      if (this.chartType === 'bar' && this.stacked) {
        return getColorShades(this.baseShade, this.count)
      }

      return ['robin', 'cherry', 'honey', 'juniper']
    },

    chartData(): Dataset[] {
      if (this.chartDataset.length) return this.chartDataset

      return Array.from(Array(this.count).keys()).map((idx) => {
        return {
          name: `Random Data #${idx}`,
          values: Array.from({ length: this.length }, () => Math.floor(10 + Math.random() * 100)),
          chartType: this.chartType
        }
      })
    },

    yAxisMin(): number {
      const allValues = this.chartData.flatMap((dataset) => dataset.values || [])

      if (allValues.length === 0) return 0

      const minValue = Math.min(...allValues)

      // For stacked charts or charts with negative values, use the min value with some padding
      // For regular charts with only positive values, use 0 as the minimum
      return minValue < 0 ? Math.floor(minValue * 1.1) : 0
    },

    yAxisMax(): number {
      const allValues = this.chartData.flatMap((dataset) => dataset.values || [])

      if (allValues.length === 0) return 100

      const maxValue = Math.max(...allValues)

      return Math.ceil(maxValue * 1.2)
    }
  }
})
